import { notify } from '@p2p/components';
import { type DeeplinkEvent, logDeeplinkEvent } from '@p2p/pages/Redirect/deeplink/deepLinkLogger';
import { forgetScheme, rememberScheme } from '@p2p/pages/Redirect/deeplink/schemeMemory';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  ALFA_MEMORY_KEY,
  ALFA_QR_ANDROID_SCHEME,
  type AlfaPlatform,
  alfaQrLink,
  buildAlfaOpenPageUrl,
  getRememberedAlfaIosScheme,
  readAlfaOpenResult,
  stripAlfaOpenResult,
} from './alfaApp';

// Сколько ждём ухода страницы в фон после перехода на схему прямо со страницы оплаты.
export const ANDROID_PROBE_MS = 1500;
export const IOS_REMEMBERED_PROBE_MS = 1000;

export const ALFA_NOT_OPENED_TEXT =
  'Приложение не открылось — скачайте QR и откройте сканер в приложении Альфа-Банка вручную';

type UseOpenAlfaAppParams = {
  orderId: string;
  platform: AlfaPlatform | null;
};

/**
 * P2P-3116: кнопка «Открыть Альфа-Банк». Android и запомненная iOS-схема открываются прямо со
 * страницы (одна попытка, по нажатию); если на iOS запомненной схемы нет или она не сработала -
 * переход на страницу перебора alfa-open.html (см. alfaApp.ts), её итог приходит обратно в адресе.
 */
export const useOpenAlfaApp = ({ orderId, platform }: UseOpenAlfaAppParams) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpening, setIsOpening] = useState(false);
  // Схема, на которую перешли со страницы и ждём ухода в фон; null - попытки нет.
  const attemptRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const log = useCallback(
    (event: DeeplinkEvent, extra: Record<string, unknown> = {}) => {
      logDeeplinkEvent(event, {
        order_id: orderId,
        bank: 'alfa',
        platform: platform ?? 'desktop',
        fromQRCode: false,
        page: 'alfa_page',
        ...extra,
      });
    },
    [orderId, platform],
  );

  // Итог перебора на alfa-open.html: пишем в лог, при неудаче - подсказка, параметры убираем из адреса.
  useEffect(() => {
    const result = readAlfaOpenResult(location.search);

    if (!result) {
      return;
    }

    if (result.result === 'ok') {
      log('deeplink_success', { scheme_name: result.scheme, attempt: result.attempt });
    } else if (result.result === 'fail') {
      log('deeplink_failed');
      notify({ type: 'warning', title: ALFA_NOT_OPENED_TEXT, timer: 8000 });
    }

    navigate(`${location.pathname}${stripAlfaOpenResult(location.search)}`, { replace: true });
  }, [location.pathname, location.search, log, navigate]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const scheme = attemptRef.current;

      if (!scheme || !document.hidden) {
        return;
      }

      // Страница ушла в фон - приложение открылось.
      attemptRef.current = null;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setIsOpening(false);
      if (platform === 'ios') {
        rememberScheme(ALFA_MEMORY_KEY, alfaQrLink(scheme));
      }
      log('deeplink_success', { scheme_name: scheme, attempt: 1 });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [log, platform]);

  const tryInPage = useCallback((scheme: string, probeMs: number, onNotOpened: () => void) => {
    attemptRef.current = scheme;
    setIsOpening(true);
    window.location.href = alfaQrLink(scheme);
    timerRef.current = setTimeout(() => {
      if (document.hidden || attemptRef.current !== scheme) {
        return;
      }
      attemptRef.current = null;
      setIsOpening(false);
      onNotOpened();
    }, probeMs);
  }, []);

  const openAlfa = useCallback(() => {
    if (!platform || attemptRef.current) {
      return;
    }

    log('deeplink_btn_click');

    if (platform === 'android') {
      tryInPage(ALFA_QR_ANDROID_SCHEME, ANDROID_PROBE_MS, () => {
        log('deeplink_failed');
        notify({ type: 'warning', title: ALFA_NOT_OPENED_TEXT, timer: 8000 });
      });
      return;
    }

    const remembered = getRememberedAlfaIosScheme();
    const openSearchPage = () =>
      window.location.assign(buildAlfaOpenPageUrl(location.pathname, remembered));

    if (!remembered) {
      openSearchPage();
      return;
    }

    // Приложение, которое открывалось в прошлый раз, удалили: забываем и ищем заново (переход на
    // страницу перебора заодно закрывает ошибку Safari от этой попытки).
    tryInPage(remembered, IOS_REMEMBERED_PROBE_MS, () => {
      forgetScheme(ALFA_MEMORY_KEY);
      openSearchPage();
    });
  }, [location.pathname, log, platform, tryInPage]);

  return { openAlfa, isOpening };
};
